// End-of-hand modal — a light DOM overlay that names the outcome (Tsumo / Ron /
// drawn hand) and offers "Next hand". It intentionally does NOT hide the table:
// the overlay is only lightly dimmed so the owner can study the hands, which the
// scene reveals face-up on canvas while the hand is over (a teaching feature).
//
// It is also where a verifiable online deal is verified from, because hand-end
// is the first moment it *can* be: the wall seed and its drand proof are
// withheld until then (`network/seatView.ts`), so the "Verify the deal" button
// only appears when `open` is handed a deal — never solo, never in a plain
// room, and never mid-hand. See `verify/verifyModel.ts`.

import { el, createAddOpponentControl, type OpponentIdentity } from '@bn-games/ui-shell';
import { t } from '../i18n/I18n.js';
import { windLabel } from './windLabel.js';
import { openVerifierModal } from '../verify/VerifierModal.js';
import type { VerifiableDeal } from '../verify/verifyModel.js';
import { scoreSection } from './handEndScore.js';
import type { HandResult } from '../game/GameState.js';
import type { Seat } from '../game/types.js';
import type { HandScore } from '../scoring/types.js';

export interface HandEndCallbacks {
  readonly onNextHand: () => void;
}

const OVERLAY_STYLE = [
  'position:fixed',
  'inset:0',
  'display:flex',
  'align-items:center',
  'justify-content:center',
  'background:rgba(6,32,24,0.35)',
  'z-index:30',
  'font-family:system-ui,-apple-system,sans-serif',
].join(';');

const CARD_STYLE = [
  'display:flex',
  'flex-direction:column',
  'align-items:center',
  'gap:0.75rem',
  'padding:1.5rem 2rem',
  'border-radius:1rem',
  'background:rgba(10,59,44,0.96)',
  'box-shadow:0 0.5rem 1.5rem rgba(0,0,0,0.5)',
  'color:#f4e6c0',
].join(';');

const TITLE_STYLE = 'margin:0;font-size:1.5rem;font-weight:700';
const SUBTITLE_STYLE = 'margin:0;font-size:1.05rem;opacity:0.85';
const BUTTON_STYLE = [
  'margin-top:0.5rem',
  'min-width:8rem',
  'padding:0.7rem 1.25rem',
  'border:none',
  'border-radius:0.5rem',
  'font-size:1rem',
  'font-weight:600',
  'cursor:pointer',
  'background:#f0d898',
  'color:#1a1614',
].join(';');
// Secondary to "Next hand": verifying is optional, and the modal's primary job
// is still to move the table on.
const VERIFY_BUTTON_STYLE = [
  'min-width:8rem',
  'padding:0.5rem 1rem',
  'border:0.0625rem solid rgba(244,230,192,0.5)',
  'border-radius:0.5rem',
  'font-size:0.9rem',
  'cursor:pointer',
  'background:transparent',
  'color:#f4e6c0',
].join(';');

/**
 * Names the winner by the wind they hold — dealer-relative, via
 * `ui/windLabel.ts`. It used to switch on the seat index directly, on the
 * belief that "seat 0 = East" was a fixed rule; it is not once Classical
 * rotates the dealer, and the modal then contradicted both the match HUD and
 * the scoring engine, which have always been dealer-relative.
 */
function titleFor(result: HandResult, dealer: Seat): string {
  if (result.kind === 'exhausted') return t('game.exhausted');
  return t('game.winner', { seat: windLabel(result.winner, dealer) });
}

function subtitleFor(result: HandResult): string {
  switch (result.kind) {
    case 'tsumo':
      return t('game.tsumo');
    case 'ron':
      return t('game.ron');
    case 'exhausted':
      return '';
  }
}

function verifyButton(deal: VerifiableDeal): HTMLElement {
  return el('button', {
    text: t('verify.openButton'),
    attrs: { type: 'button', style: VERIFY_BUTTON_STYLE },
    on: { click: () => openVerifierModal(deal) },
  });
}

export class HandEndModal {
  private overlay: HTMLElement | null = null;

  constructor(private readonly callbacks: HandEndCallbacks) {}

  isOpen(): boolean {
    return this.overlay !== null;
  }

  /**
   * `dealer` is who dealt this hand — it decides which wind names the winner,
   * so `DEALER` (seat 0) is right only for Simplified, which never rotates it.
   * `deal` is non-null only for a verifiable online room's finished hand.
   * `opponents` is every other human at the table this hand, `[]` solo — shown
   * on every online hand-end (not gated on match completion, unlike domino's
   * result modal): this modal is destroyed and rebuilt fresh every hand
   * regardless, so there is no in-place rebuild to protect the control's
   * transient state from, and Simplified online play has no match state at
   * all to gate on.
   */
  open(
    result: HandResult,
    handScore: HandScore | null,
    dealer: Seat,
    deal: VerifiableDeal | null = null,
    opponents: readonly OpponentIdentity[] = [],
  ): void {
    if (this.isOpen()) return;
    const card = el('div', { attrs: { style: CARD_STYLE } }, [
      el('h2', { text: titleFor(result, dealer), attrs: { style: TITLE_STYLE } }),
      el('p', { text: subtitleFor(result), attrs: { style: SUBTITLE_STYLE } }),
      handScore !== null ? scoreSection(handScore) : null,
      el('button', {
        text: t('game.nextHand'),
        attrs: { type: 'button', style: BUTTON_STYLE },
        on: { click: () => this.handleNext() },
      }),
      deal !== null ? verifyButton(deal) : null,
      createAddOpponentControl(opponents),
    ]);
    const overlay = el('div', { attrs: { style: OVERLAY_STYLE } }, [card]);
    document.body.appendChild(overlay);
    this.overlay = overlay;
  }

  close(): void {
    if (this.overlay !== null) {
      this.overlay.remove();
      this.overlay = null;
    }
  }

  private handleNext(): void {
    this.close();
    this.callbacks.onNextHand();
  }
}
